import { DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import type { WorkspaceFolder, WorkspaceProject } from "@/components/workspace/workspace-types";
import { useWorkspaceData } from "@/hooks/use-workspace-data";
/**
 * Project & folder action menus. Each item triggers a phase-2 toast today
 * (the dialogs that will collect the rename / delete confirmation land in
 * later patches of Stage 2; this file is the simplest cohesive unit).
 */
import { Copy, ExternalLink, FolderInput, FolderOpen, Trash2 } from "lucide-react";

export interface WorkspaceActionMenuProps {
  project?: WorkspaceProject;
  folder?: WorkspaceFolder;
}

export function WorkspaceActionMenu({ project, folder }: WorkspaceActionMenuProps): JSX.Element {
  const renameProject = useWorkspaceData((s) => s.renameProject);
  const moveProject = useWorkspaceData((s) => s.moveProject);
  const deleteProject = useWorkspaceData((s) => s.deleteProject);
  const renameFolder = useWorkspaceData((s) => s.renameFolder);
  const moveFolder = useWorkspaceData((s) => s.moveFolder);
  const deleteFolder = useWorkspaceData((s) => s.deleteFolder);

  if (project) {
    return (
      <>
        <DropdownMenuLabel>{project.name}</DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void navigator.clipboard?.writeText(`/project/${project.id}`).catch(() => {});
          }}
        >
          <Copy className="mr-2 h-4 w-4" />
          Copy project link
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            window.open(`/project/${project.id}`, "_blank", "noopener");
          }}
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          Open in new tab
        </DropdownMenuItem>
        <Separator className="my-1" />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void renameProject({ id: project.id, name: project.name }).catch(() => {});
          }}
        >
          Rename project
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void moveProject({ id: project.id, folder_id: project.folder_id }).catch(() => {});
          }}
        >
          <FolderInput className="mr-2 h-4 w-4" />
          Move to folder…
        </DropdownMenuItem>
        <Separator className="my-1" />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void deleteProject({ id: project.id }).catch(() => {});
          }}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete project
        </DropdownMenuItem>
      </>
    );
  }

  if (folder) {
    return (
      <>
        <DropdownMenuLabel>{folder.name}</DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void renameFolder({ id: folder.id, name: folder.name }).catch(() => {});
          }}
        >
          Rename folder
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void moveFolder({ id: folder.id, parent_id: folder.parent_id }).catch(() => {});
          }}
        >
          <FolderOpen className="mr-2 h-4 w-4" />
          Move folder…
        </DropdownMenuItem>
        <Separator className="my-1" />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void deleteFolder({ id: folder.id }).catch(() => {});
          }}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete folder
        </DropdownMenuItem>
      </>
    );
  }

  return <DropdownMenuItem disabled>No selection</DropdownMenuItem>;
}
